import { Loading } from '@affine/component/ui/loading';
import { i18nTime, isI18nString, useI18n } from '@affine/i18n';
import { SearchIcon } from '@blocksuite/icons/rc';
import {
  isWithinWorkspaceViewCodePointLimit,
  type WorkspaceViewScope,
} from '@streamlain/copilot-realtime-contract';
import { LiveData, useLiveData, useService } from '@toeverything/infra';
import clsx from 'clsx';
import { Command } from 'cmdk';
import {
  type ComponentProps,
  createContext,
  forwardRef,
  type ReactElement,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
} from 'react';

import type { AffineCommand } from '../../../commands/registry/command';
import {
  UiNavigationItem,
  useUiNavigationDefinition,
} from '../../streamlain/copilot/ui-navigation-item';
import type { UiNavigationDefinition } from '../../streamlain/copilot/workspace-navigation-catalog';
import { WorkspaceViewContextService } from '../../streamlain/copilot/workspace-view-context';
import { CMDKQuickSearchService } from '../services/cmdk';
import { QuickSearchService } from '../services/quick-search';
import type { QuickSearchGroup } from '../types/group';
import type { QuickSearchItem } from '../types/item';
import {
  QUICK_SEARCH_RESULT_OWNER,
  quickSearchResultDefinitionId,
} from '../types/item';
import * as styles from './cmdk.css';
import { DesktopSearchHint } from './desktop-search-hint';
import { HighlightText } from './highlight-text';
import {
  OnlineSearchFooter,
  type OnlineSearchFooterProps,
} from './online-search-footer';
import {
  searchResultRowAttributes,
  SearchResultRowContent,
  SelectionCursor,
} from './search-result-row';
import * as searchResults from './search-results.css';
import * as searchUi from './search-ui.css';

type Groups = { group?: QuickSearchGroup; items: QuickSearchItem[] }[];

export const CMDK = ({
  className,
  query,
  groups: newGroups = [],
  error,
  status,
  footer,
  desktopHint = false,
  inputLabel,
  placeholder,
  loading: newLoading = false,
  loadingProgress,
  onQueryChange,
  onSubmit,
  filters,
  navigationScope,
  navigationEnabled = true,
}: React.PropsWithChildren<{
  className?: string;
  filters?: ReactNode;
  navigationScope?: WorkspaceViewScope;
  navigationEnabled?: boolean;
  query: string;
  error?: ReactNode;
  status?: ReactNode;
  /** Under the results: a note, or a button when `onClick` is set. */
  footer?: OnlineSearchFooterProps | null;
  desktopHint?: boolean;
  inputLabel?: ReactNode;
  placeholder?: string;
  loading?: boolean;
  loadingProgress?: number;
  groups?: Groups;
  onSubmit?: (item: QuickSearchItem) => void;
  onQueryChange?: (query: string) => void;
}>) => {
  const [opening, setOpening] = useState(false);
  const [loading, setLoading] = useState(false);

  const [{ groups, selectedValue }, dispatch] = useReducer(
    (
      state: {
        groups: Groups;
        selectedValue: string;
      },
      action:
        | { type: 'select'; payload: string }
        | { type: 'reset-select' }
        | { type: 'update-groups'; payload: Groups }
    ) => {
      // control the currently selected item so that when the item list changes, the selected item remains controllable
      if (action.type === 'select') {
        return {
          ...state,
          selectedValue: action.payload,
        };
      }
      if (action.type === 'reset-select') {
        // reset selected item to the first item
        const firstItem = state.groups.at(0)?.items.at(0)?.id;
        return {
          ...state,
          selectedValue: firstItem ?? '',
        };
      }
      if (action.type === 'update-groups') {
        const prevGroups = state.groups;
        const prevSelectedValue = state.selectedValue;

        const prevFirstItem = prevGroups.at(0)?.items.at(0)?.id;
        const newFirstItem = action.payload.at(0)?.items.at(0)?.id;
        const isSelectingFirstItem = prevSelectedValue === prevFirstItem;
        // if previous selected item is the first item, select the new first item
        if (isSelectingFirstItem) {
          return {
            ...state,
            groups: action.payload,
            selectedValue: newFirstItem ?? '',
          };
        }

        const selectedExists = action.payload.some(({ items }) =>
          items.some(item => item.id === prevSelectedValue)
        );
        // if previous selected item exists in the new list, keep it
        if (selectedExists) {
          return {
            ...state,
            groups: action.payload,
            selectedValue: prevSelectedValue,
          };
        }

        // if previous selected item does not exist in the new list, select the new first item
        return {
          ...state,
          groups: action.payload,
          selectedValue: newFirstItem ?? '',
        };
      }
      return state;
    },
    { groups: [], selectedValue: '' }
  );

  const listRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // fix list height animation on opening
  useLayoutEffect(() => {
    setOpening(true);
    const timeout = setTimeout(() => {
      setOpening(false);
      inputRef.current?.focus();
    }, 150);
    return () => {
      clearTimeout(timeout);
    };
  }, []);

  const handleValueChange = useCallback(
    (query: string) => {
      onQueryChange?.(query);
      dispatch({
        type: 'reset-select',
      });
      requestAnimationFrame(() => {
        if (listRef.current) listRef.current.scrollTop = 0;
      });
    },
    [onQueryChange]
  );

  const handleSelectChange = useCallback(
    (value: string) => {
      dispatch({
        type: 'select',
        payload: value,
      });
    },
    [dispatch]
  );

  useEffect(() => {
    // on group change
    dispatch({
      type: 'update-groups',
      payload: newGroups,
    });
  }, [newGroups]);

  useEffect(() => {
    // debounce loading state
    const timeout = setTimeout(() => setLoading(newLoading), 1000);
    return () => clearTimeout(timeout);
  }, [newLoading]);

  return (
    <Command
      data-testid="cmdk-quick-search"
      shouldFilter={false}
      className={clsx(className, styles.root, styles.panelContainer)}
      value={selectedValue}
      onValueChange={handleSelectChange}
      loop
    >
      {inputLabel ? (
        <div className={styles.pageTitleWrapper}>
          <span className={styles.pageTitle}>{inputLabel}</span>
        </div>
      ) : null}
      <div
        className={clsx(className, styles.searchInputContainer, {
          [styles.hasInputLabel]: inputLabel,
        })}
      >
        <div className={searchUi.field}>
          <SearchIcon />
          <Command.Input
            placeholder={placeholder}
            ref={inputRef}
            value={query}
            onValueChange={handleValueChange}
            className={clsx(className, searchUi.fieldInput)}
          />
          {loading ? (
            <Loading
              size={20}
              progress={
                loadingProgress ? Math.max(loadingProgress, 0.2) : undefined
              }
              speed={loadingProgress ? 0 : undefined}
            />
          ) : null}
        </div>
      </div>

      {filters ? <div className={styles.filters}>{filters}</div> : null}
      <ContentNavigation
        enabled={navigationEnabled}
        scope={navigationScope}
        groups={groups}
        settled={groups === newGroups}
      >
        <Command.List ref={listRef} data-opening={opening ? true : undefined}>
          <SelectionCursor rowSelector="[cmdk-item]" />
          {error && <p className={styles.errorMessage}>{error}</p>}
          {groups.map(({ group, items }) => {
            return (
              <CMDKGroup
                key={group?.id ?? ''}
                onSubmit={onSubmit}
                query={query}
                group={{ group, items }}
              />
            );
          })}
        </Command.List>
      </ContentNavigation>
      {status || footer ? (
        <div className={styles.footer}>
          {status ? <span className={styles.statusPill}>{status}</span> : null}
          {footer ? <OnlineSearchFooter {...footer} /> : null}
        </div>
      ) : null}
      <DesktopSearchHint visible={desktopHint} />
    </Command>
  );
};

const SearchNavigationEnabled = createContext(true);
export const ContentNavigationScope = createContext<{
  scope: WorkspaceViewScope;
  generation: number;
} | null>(null);
export function ContentNavigation({
  enabled = true,
  scope,
  groups,
  settled,
  children,
}: {
  enabled?: boolean;
  scope?: WorkspaceViewScope;
  groups: Groups;
  settled: boolean;
  children: ReactNode;
}) {
  if (!enabled)
    return (
      <SearchNavigationEnabled.Provider value={false}>
        {children}
      </SearchNavigationEnabled.Provider>
    );
  return scope ? (
    <ScopedContentNavigation scope={scope} groups={groups} settled={settled}>
      {children}
    </ScopedContentNavigation>
  ) : (
    <>{children}</>
  );
}
function ScopedContentNavigation({
  scope,
  groups,
  settled,
  children,
}: {
  scope: WorkspaceViewScope;
  groups: Groups;
  settled: boolean;
  children: ReactNode;
}) {
  const cmdk = useService(CMDKQuickSearchService),
    context = useService(WorkspaceViewContextService);
  const quick = useService(QuickSearchService).quickSearch;
  const generation = useLiveData(quick.queryGeneration$),
    loading = useLiveData(quick.isLoading$);
  const settledSource = useLiveData(quick.isSettled$);
  const i18n = useI18n();
  // Memoize the bounded normal result projection; command-registry notifications must not republish unchanged content.
  const previous = useRef<{
    scope: WorkspaceViewScope;
    generation: number | null;
    loading: boolean;
    settledSource: boolean;
    i18n: typeof i18n;
    items: QuickSearchItem[];
    definitions: readonly UiNavigationDefinition[];
  } | null>(null);
  const items: QuickSearchItem[] = [];
  let visits = 0;
  outer: for (const group of groups)
    for (const item of group.items) {
      if (visits++ >= 128) break outer;
      if (item.source !== 'commands') items.push(item);
    }
  const last = previous.current;
  if (
    !last ||
    last.scope.workspaceId !== scope.workspaceId ||
    last.scope.viewId !== scope.viewId ||
    last.generation !== generation ||
    last.loading !== loading ||
    last.settledSource !== settledSource ||
    last.i18n !== i18n ||
    last.items.length !== items.length ||
    items.some((item, index) => item !== last.items[index])
  ) {
    previous.current = {
      scope,
      generation,
      loading,
      settledSource,
      i18n,
      items,
      definitions: cmdk.createContentNavigation(scope, items, item => {
        const raw = isI18nString(item.label) ? item.label : item.label.title;
        if (
          typeof raw === 'string' &&
          !isWithinWorkspaceViewCodePointLimit(raw, 256)
        )
          return '';
        const text = i18n.t(raw);
        if (!isWithinWorkspaceViewCodePointLimit(text, 256)) return '';
        return text.replaceAll('<b>', '').replaceAll('</b>', '');
      }),
    };
  }
  const definitions = previous.current!.definitions;
  const items$ = useMemo(
    () => new LiveData<readonly UiNavigationDefinition[]>([]),
    []
  );
  useLayoutEffect(
    () =>
      context.navigation.registerSource({
        scope,
        ownerId: QUICK_SEARCH_RESULT_OWNER,
        items$,
      }),
    [context, scope, items$]
  );
  useLayoutEffect(() => items$.next(definitions), [items$, definitions]);
  useLayoutEffect(() => {
    const rendered =
      generation === null || loading || !settledSource || !settled
        ? null
        : { scope, generation };
    cmdk.contentResultsGeneration$.next(rendered);
    return () => {
      if (cmdk.contentResultsGeneration$.value === rendered)
        cmdk.contentResultsGeneration$.next(null);
    };
  }, [cmdk, definitions, generation, loading, settledSource, scope, settled]);
  const value = useMemo(
    () => (generation === null ? null : { scope, generation }),
    [scope, generation]
  );
  return (
    <ContentNavigationScope.Provider value={value}>
      {children}
    </ContentNavigationScope.Provider>
  );
}
// cmdk owns keyboard selection and its internal click handler. While the shared
// binding hands in its click, capture the native click once so the binding can
// supply args without cmdk submitting twice; without it cmdk handles the click.
const NavigationCommandItem = forwardRef<
  HTMLDivElement,
  ComponentProps<typeof Command.Item>
>(function NavigationCommandItem({ onClick, ...props }, ref) {
  return (
    <Command.Item
      {...props}
      ref={ref}
      onClickCapture={
        onClick
          ? event => {
              event.stopPropagation();
              onClick(event);
            }
          : undefined
      }
    />
  );
});
function CommandResultItem({
  item,
  onSubmit,
  ...props
}: Omit<ComponentProps<typeof Command.Item>, 'onSubmit'> & {
  item: QuickSearchItem;
  onSubmit?: (item: QuickSearchItem) => void;
}) {
  return (
    <SearchResultNavigation item={item} onSubmit={onSubmit}>
      <NavigationCommandItem {...props} onSelect={() => onSubmit?.(item)} />
    </SearchResultNavigation>
  );
}

/**
 * A result row keeps one element tree while the search loads: a content row
 * waits for its definition inside the same navigation item instead of
 * swapping elements, which would mount the row again and replay its entrance.
 */
export function SearchResultNavigation({
  item,
  onSubmit,
  children,
  userActivation = 'click',
}: {
  item: QuickSearchItem;
  onSubmit?: (item: QuickSearchItem) => void;
  children: ReactElement;
  userActivation?: 'click' | 'double-click';
}) {
  const enabled = useContext(SearchNavigationEnabled);
  const contentScope = useContext(ContentNavigationScope);
  const command =
    item.source === 'commands'
      ? (item.payload as AffineCommand | undefined)
      : undefined;
  const navigation = command?.navigation;
  const definition = useMemo(
    () =>
      navigation
        ? { ...navigation.definition, activate: () => onSubmit?.(item) }
        : null,
    [navigation, onSubmit, item]
  );
  if (!enabled) return children;
  if (navigation && definition)
    return (
      <UiNavigationItem
        scope={navigation.scope}
        ownerId={navigation.ownerId}
        definition={definition}
        userActivation={userActivation}
      >
        {children}
      </UiNavigationItem>
    );
  if (
    item.source !== 'commands' &&
    isWithinWorkspaceViewCodePointLimit(item.id, 256)
  )
    return (
      <ContentResultNavigation
        content={contentScope}
        item={item}
        userActivation={userActivation}
      >
        {children}
      </ContentResultNavigation>
    );
  return children;
}

function ContentResultNavigation({
  content,
  item,
  children,
  userActivation,
}: {
  /** Null while the content results of the query are not rendered yet. */
  content: { scope: WorkspaceViewScope; generation: number } | null;
  item: QuickSearchItem;
  children: ReactElement;
  userActivation: 'click' | 'double-click';
}) {
  const definition = useUiNavigationDefinition(
    content?.scope ?? null,
    QUICK_SEARCH_RESULT_OWNER,
    content ? quickSearchResultDefinitionId(content.generation, item.id) : ''
  );
  const target =
    content && definition
      ? { scope: content.scope, definition }
      : { scope: content?.scope ?? null, definition: null };
  return (
    <UiNavigationItem
      {...target}
      ownerId={QUICK_SEARCH_RESULT_OWNER}
      userActivation={userActivation}
    >
      {children}
    </UiNavigationItem>
  );
}

export const CMDKGroup = ({
  group: { group, items },
  onSubmit,
  query,
}: {
  group: { group?: QuickSearchGroup; items: QuickSearchItem[] };
  onSubmit?: (item: QuickSearchItem) => void;
  query: string;
}) => {
  const i18n = useI18n();
  return (
    <Command.Group
      key={query + ':' + (group?.id ?? '')}
      heading={group && i18n.t(group.label)}
      style={{ overflowAnchor: 'none' }}
    >
      {items.map((item, index) => {
        const [title, subTitle] = isI18nString(item.label)
          ? [i18n.t(item.label), null]
          : [
              i18n.t(item.label.title),
              item.label.subTitle ? i18n.t(item.label.subTitle) : null,
            ];

        return (
          <CommandResultItem
            key={item.id}
            item={item}
            onSubmit={onSubmit}
            value={item.id}
            disabled={item.disabled}
            data-is-danger={
              item.id === 'editor:page-move-to-trash' ||
              item.id === 'editor:edgeless-move-to-trash'
            }
            {...(item.row
              ? {
                  ...searchResultRowAttributes(items, index),
                  className: searchResults.row,
                }
              : {})}
          >
            {item.row ? (
              <SearchResultRowContent item={item} />
            ) : (
              <>
                <div className={styles.itemIcon}>
                  {item.icon &&
                    (typeof item.icon === 'function' ? (
                      <item.icon />
                    ) : (
                      item.icon
                    ))}
                </div>
                <div
                  data-testid="cmdk-label"
                  className={styles.itemLabel}
                  data-value={item.id}
                >
                  <div className={styles.itemTitle}>
                    <HighlightText text={title} start="<b>" end="</b>" />
                    {item.badge ? (
                      <span className={styles.itemBadge}>
                        {i18n.t(item.badge)}
                      </span>
                    ) : null}
                  </div>
                  {subTitle && (
                    <div className={styles.itemSubtitle}>
                      <HighlightText text={subTitle} start="<b>" end="</b>" />
                    </div>
                  )}
                </div>
                {item.timestamp ? (
                  <div className={styles.timestamp}>
                    {i18nTime(new Date(item.timestamp))}
                  </div>
                ) : null}
                {item.keyBinding ? (
                  <CMDKKeyBinding keyBinding={item.keyBinding} />
                ) : null}
              </>
            )}
          </CommandResultItem>
        );
      })}
    </Command.Group>
  );
};

const CMDKKeyBinding = ({ keyBinding }: { keyBinding: string }) => {
  const isMacOS = environment.isMacOs;
  const fragments = useMemo(() => {
    return keyBinding.split('+').map(fragment => {
      if (fragment === '$mod') {
        return isMacOS ? '⌘' : 'Ctrl';
      }
      if (fragment === 'Alt') {
        return isMacOS ? '⌥' : 'Alt';
      }
      if (fragment.startsWith('Key')) {
        return fragment.slice(3);
      }
      if (fragment === 'ArrowUp') {
        return '↑';
      }
      if (fragment === 'ArrowDown') {
        return '↓';
      }
      if (fragment === 'ArrowLeft') {
        return '←';
      }
      if (fragment === 'ArrowRight') {
        return '→';
      }
      return fragment;
    });
  }, [isMacOS, keyBinding]);

  return (
    <div className={styles.keybinding}>
      {fragments.map(fragment => {
        return (
          <div key={fragment} className={styles.keybindingFragment}>
            {fragment}
          </div>
        );
      })}
    </div>
  );
};
